import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserPlus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";

export const ManualUserAddDialog = ({ onSuccess }: { onSuccess?: () => void }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [accessLevel, setAccessLevel] = useState("full_access");
  const [notes, setNotes] = useState("");
  const [sendPasswordEmail, setSendPasswordEmail] = useState(true);
  const [skipPayment, setSkipPayment] = useState(true);

  const handleAddUser = async () => {
    // Validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!fullName.trim()) {
      toast.error("Please enter the user's full name");
      return;
    }
    if (!email || !emailRegex.test(email)) {
      toast.error("Please enter a valid email address");
      return;
    }
    if (!sendPasswordEmail && password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    setLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('manual-add-user', {
        body: { 
          fullName: fullName.trim(),
          email: email.trim().toLowerCase(),
          password: sendPasswordEmail ? null : password,
          accessLevel,
          notes: notes.trim() || null,
          sendPasswordEmail,
          skipPayment
        }
      });

      if (error) throw error;

      if (data.success) {
        toast.success(`User ${fullName} added successfully!`);
        setFullName("");
        setEmail("");
        setPassword("");
        setNotes("");
        setAccessLevel("full_access");
        setSendPasswordEmail(true);
        setSkipPayment(true);
        setOpen(false);
        onSuccess?.();
      } else {
        toast.error(data.error || "Failed to add user");
      }
    } catch (error: any) {
      console.error('Error adding user:', error);
      toast.error(error.message || "Failed to add user. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2">
          <UserPlus className="w-4 h-4" />
          Add User Manually
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add User Manually</DialogTitle>
          <DialogDescription>
            Directly create a user account without sending an invitation.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="fullName">Full Name *</Label>
            <Input
              id="fullName"
              type="text"
              placeholder="John Doe"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              disabled={loading}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email Address *</Label>
            <Input
              id="email"
              type="email"
              placeholder="user@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="accessLevel">Access Level *</Label>
            <Select value={accessLevel} onValueChange={setAccessLevel} disabled={loading}>
              <SelectTrigger id="accessLevel">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="full_access">Full Access - Dashboard & Community</SelectItem>
                <SelectItem value="community_only">Community Only</SelectItem>
                <SelectItem value="dashboard_only">Dashboard Only</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-3 p-3 border rounded-lg bg-muted/50">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="sendPasswordEmail"
                checked={sendPasswordEmail}
                onCheckedChange={(checked) => setSendPasswordEmail(checked as boolean)}
                disabled={loading}
              />
              <Label htmlFor="sendPasswordEmail" className="cursor-pointer">
                Send password setup email to user
              </Label>
            </div>

            {!sendPasswordEmail && (
              <div className="space-y-2 ml-6">
                <Label htmlFor="password">Set Password *</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter password (min 8 characters)"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  minLength={8}
                />
                <p className="text-xs text-muted-foreground">
                  User will need to use this password to sign in
                </p>
              </div>
            )}
          </div>

          <div className="flex items-center space-x-2 p-3 border rounded-lg bg-muted/50">
            <Checkbox
              id="skipPayment"
              checked={skipPayment}
              onCheckedChange={(checked) => setSkipPayment(checked as boolean)}
              disabled={loading}
            />
            <Label htmlFor="skipPayment" className="cursor-pointer">
              Grant free access (skip payment requirement)
            </Label>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Admin Notes (Optional)</Label>
            <Textarea
              id="notes"
              placeholder="Add notes about this user..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={loading}
              rows={3}
            />
          </div>

          <div className="bg-blue-50 dark:bg-blue-950/20 p-3 rounded-md text-sm">
            <p className="font-medium mb-1">ℹ️ What happens:</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              <li>User account is created immediately</li>
              <li>User appears in system with selected access level</li>
              {sendPasswordEmail ? (
                <li>User receives email to set their password</li>
              ) : (
                <li>User can sign in with the password you set</li>
              )}
              {skipPayment && <li>User gets immediate access without payment</li>}
            </ul>
          </div>
        </div>

        <div className="flex gap-3 justify-end">
          <Button variant="outline" onClick={() => setOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleAddUser} disabled={loading}>
            {loading ? "Adding User..." : "Add User"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};